/**
 * The chart contract: what a chart type is, what options it exposes, and what a card needs to
 * render one. `common.ts` behind this is the chart implementation helpers.
 */
export const CHART_ASPECT_RATIOS = {
  portrait: 9 / 16,
  landscape: 16 / 9,
  goldenPortrait: 1 / 1.618,
  goldenLandscape: 1.618,
  photoPortrait: 5 / 7,
  photoLandscape: 7 / 5,
  square: 1,
} as const;

// Centralized icon sizing

export type ChartFontStyle = {
  title: { size: 16; bold: true };
  legend: { size: 12; bold: true };
  valueAxis: { size: 10; bold: true };
  categoryAxis: { size: 11; bold: false };
};

export type ChartShape = keyof typeof CHART_ASPECT_RATIOS;

export type ChartSize = "small" | "medium" | "large" | "full";

export type ChartOptionType = "boolean" | "dropDown" | "text" | "toggle";

export type ChartOptionChangeHandler = (worksheetName: string, chartName: string, value: any) => Promise<void>;

export type ChartOptionsGetter = (worksheetName: string, chartName: string) => Promise<Record<string, ChartOption>>;

export type ChartBuilder = (chartOptions: Record<string, ChartOption>) => Promise<Excel.Chart | undefined>;

export type ChartRefresher = (
  worksheetName: string,
  chartName: string,
  options?: Record<string, ChartOption>
) => Promise<void>;

/** The icon a chart type shows, as an asset the taskpane renders — `excel/` stays free of React. */
export interface ChartIcon {
  /** Path under `assets/`, copied not bundled — see code-organization.md. */
  src: string;
  alt: string;
  /** Quarter turn — the bar and column orientations share one asset shown two ways. */
  rotated?: boolean;
}

export type ChartIconGetter = (chartType?: Excel.ChartType) => ChartIcon;

export type ChartOptionItem = {
  key: string;
  label: string;
  icon?: ChartIcon;
  colIndex?: number;
  suffix?: string;
};

export interface ChartOption {
  key: string;
  label: string;
  type: ChartOptionType;
  selectedOptionItem: ChartOptionItem;
  optionItems: Record<string, ChartOptionItem>;
  onChange?: (worksheetName: string, chartName: string, option: ChartOptionItem) => Promise<void>;
  getSelectedOptionItem?: (worksheetName: string, chartName: string) => Promise<ChartOptionItem>;
}

export interface EdiphiChartType {
  key: string;
  title: string;
  chartBuilder: ChartBuilder;
  chartRefresher: ChartRefresher;
  chartIconGetter: ChartIconGetter;
  getOptions: ChartOptionsGetter;
}

/** One chart discovered in the workbook, resolved down to what a card needs to render it. */

export interface ChartCardProps {
  worksheetName: string;
  chartName: string;
  ediphiChartType: EdiphiChartType;
  chartOptions: Record<string, ChartOption>;
  title: string;
  subTitle: string;
}

//this is not actually deleting the tables yet, bc it disrupts the user with a scary dialog
